import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "../api";
import Modal from "../components/Modal";
import PatientPicker from "../components/PatientPicker";
import TreatmentSelect from "../components/TreatmentSelect";
import { inputCls, labelCls, toApiDate, toApiTime, fromApiDate, fromApiTime } from "../utils/datetime";

const EMPTY = { patient: "", patient_id: "", treatment: "", due_date: "", time: "", notes: "" };
const STATUS_STYLE = { Due: "bg-gold-soft/70 text-pine-deep", Done: "bg-teal-soft text-teal-brand", Missed: "bg-red-50 text-red-500" };

export default function Followups() {
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [picked, setPicked] = useState(null);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState(EMPTY);
  const me = JSON.parse(localStorage.getItem("dyntra_connect_user") || "{}");
  const canDelete = ["super_admin", "owner", "admin", "clinic_manager"].includes(me.level || "");

  const load = () => api.get("/connect/list/followups").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post("/connect/followups", { ...form, due_date: toApiDate(form.due_date), time: toApiTime(form.time) });
      toast.success("Follow-up created");
      setForm(EMPTY); setPicked(null); setShowForm(false); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not create follow-up"); }
  };

  const openEdit = (f) => {
    setEditing(f);
    setEditForm({
      patient: f.patient || "", patient_id: f.patient_id || "", treatment: f.treatment || "",
      due_date: fromApiDate(f.due_date), time: fromApiTime(f.time), notes: f.notes || "", status: f.status || "Due",
    });
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    try {
      await api.patch(`/connect/followups/${editing.id}`, { ...editForm, due_date: toApiDate(editForm.due_date), time: toApiTime(editForm.time) });
      toast.success("Follow-up updated");
      setEditing(null); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not update follow-up"); }
  };

  const remove = async (id) => {
    try { await api.delete(`/connect/followups/${id}`); toast.success("Follow-up removed"); } catch { toast.error("Could not remove"); }
    load();
  };

  return (
    <div data-testid="connect-followups" className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="font-display text-2xl font-semibold text-pine-deep">Follow-ups</h2>
          <p className="text-sm text-slate-500">Next visits, review appointments and post-treatment check-ins.</p>
        </div>
        <button data-testid="followup-add-toggle" onClick={() => setShowForm(!showForm)}
          className="inline-flex items-center gap-2 rounded-full bg-teal-brand px-5 py-2.5 text-xs font-bold text-white hover:bg-pine transition-all">
          <Plus className="h-4 w-4" /> New Follow-up
        </button>
      </div>

      {showForm && (
        <form data-testid="followup-add-form" onSubmit={add} className="rounded-2xl bg-white border border-pine/8 shadow-soft p-5 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <div className="sm:col-span-2">
            <label className={labelCls}>Patient *</label>
            <div className="mt-1">
              <PatientPicker testid="followup-patient-picker" value={picked}
                onSelect={(p) => { setPicked(p); if (p) setForm({ ...form, patient: p.name, patient_id: p.patient_id || "" }); }} />
            </div>
            {!picked && (
              <input required data-testid="followup-patient-name" placeholder="Patient name" value={form.patient}
                onChange={(e) => setForm({ ...form, patient: e.target.value })} className={`mt-2 ${inputCls}`} />
            )}
          </div>
          <div>
            <label className={labelCls}>Treatment</label>
            <div className="mt-1"><TreatmentSelect testid="followup-add-treatment" value={form.treatment} onChange={(v) => setForm({ ...form, treatment: v })} /></div>
          </div>
          <div>
            <label className={labelCls}>Follow-up Date *</label>
            <input required type="date" data-testid="followup-add-date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className={`mt-1 ${inputCls}`} />
          </div>
          <div>
            <label className={labelCls}>Time</label>
            <input type="time" data-testid="followup-add-time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} className={`mt-1 ${inputCls}`} />
          </div>
          <div className="sm:col-span-2 lg:col-span-3">
            <label className={labelCls}>Notes</label>
            <textarea data-testid="followup-add-notes" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className="mt-1 w-full rounded-xl border border-pine/15 px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-pine bg-white" />
          </div>
          <div>
            <button type="submit" data-testid="followup-submit" className="h-11 w-full rounded-full bg-pine text-ivory text-xs font-bold hover:bg-pine-deep transition-colors">Save Follow-up</button>
          </div>
        </form>
      )}

      <div className="rounded-2xl bg-white border border-pine/8 shadow-soft overflow-hidden">
        <div className="overflow-x-auto" data-lenis-prevent>
          <table className="w-full text-sm min-w-[640px]">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wider text-slate-400 border-b border-pine/10 bg-cream/40">
                <th className="px-5 py-3.5 font-bold">Patient</th>
                <th className="px-5 py-3.5 font-bold">Treatment</th>
                <th className="px-5 py-3.5 font-bold">Follow-up Date</th>
                <th className="px-5 py-3.5 font-bold">Time</th>
                <th className="px-5 py-3.5 font-bold">Status</th>
                <th className="px-5 py-3.5 font-bold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((f) => (
                <tr key={f.id} data-testid={`followup-row-${f.id.slice(0, 8)}`} className="border-b border-pine/5 last:border-0 hover:bg-cream/30 cursor-pointer" onClick={() => openEdit(f)}>
                  <td className="px-5 py-3.5 font-semibold text-pine-deep">{f.patient} {f.patient_id && <span className="text-[10px] text-gold font-bold">{f.patient_id}</span>}</td>
                  <td className="px-5 py-3.5">{f.treatment || "—"}</td>
                  <td className="px-5 py-3.5">{f.due_date}</td>
                  <td className="px-5 py-3.5">{f.time || "—"}</td>
                  <td className="px-5 py-3.5"><span className={`rounded-full px-2.5 py-1 text-[10px] font-bold ${STATUS_STYLE[f.status] || STATUS_STYLE.Due}`}>{f.status}</span></td>
                  <td className="px-5 py-3.5" onClick={(e) => e.stopPropagation()}>
                    <div className="flex gap-2">
                      <button data-testid={`followup-edit-${f.id.slice(0, 8)}`} onClick={() => openEdit(f)} className="h-7 w-7 rounded-full border border-pine/15 flex items-center justify-center text-pine hover:bg-cream" aria-label="Edit follow-up"><Pencil className="h-3.5 w-3.5" /></button>
                      {canDelete && <button data-testid={`followup-delete-${f.id.slice(0, 8)}`} onClick={() => remove(f.id)} className="h-7 w-7 rounded-full border border-red-200 flex items-center justify-center text-red-500 hover:bg-red-50" aria-label="Delete follow-up"><Trash2 className="h-3.5 w-3.5" /></button>}
                    </div>
                  </td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr><td colSpan={6} className="px-5 py-10 text-center text-slate-400">No follow-ups yet — create one from an appointment or the button above.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {editing && (
        <Modal testid="followup-edit-modal" onClose={() => setEditing(null)} title={editing.patient}
          subtitle={`${editing.patient_id || "—"} · ${editing.treatment || "Follow-up"}`}>
          <form onSubmit={saveEdit} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Follow-up Date</label>
                <input required type="date" data-testid="followup-edit-date" value={editForm.due_date} onChange={(e) => setEditForm({ ...editForm, due_date: e.target.value })} className={`mt-1 ${inputCls}`} />
              </div>
              <div>
                <label className={labelCls}>Time</label>
                <input type="time" data-testid="followup-edit-time" value={editForm.time} onChange={(e) => setEditForm({ ...editForm, time: e.target.value })} className={`mt-1 ${inputCls}`} />
              </div>
            </div>
            <div>
              <label className={labelCls}>Treatment</label>
              <div className="mt-1"><TreatmentSelect testid="followup-edit-treatment" value={editForm.treatment} onChange={(v) => setEditForm({ ...editForm, treatment: v })} /></div>
            </div>
            <div>
              <label className={labelCls}>Status</label>
              <select data-testid="followup-edit-status" value={editForm.status} onChange={(e) => setEditForm({ ...editForm, status: e.target.value })} className={`mt-1 ${inputCls}`}>
                {["Due", "Done", "Missed"].map((s) => <option key={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Notes</label>
              <textarea data-testid="followup-edit-notes" rows={2} value={editForm.notes} onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                className="mt-1 w-full rounded-xl border border-pine/15 px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-pine bg-white" />
            </div>
            <div className="flex gap-3">
              <button type="submit" data-testid="followup-edit-save" className="flex-1 rounded-full bg-pine px-5 py-3 text-sm font-bold text-ivory hover:bg-pine-deep transition-colors">Save Changes</button>
              <button type="button" onClick={() => setEditing(null)} className="rounded-full border border-pine/20 px-5 py-3 text-sm font-bold text-pine hover:bg-cream transition-colors">Cancel</button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
